import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
    ArrowLeft,
    CloudSun,
    Code2,
    Inbox,
    RefreshCw,
    RotateCcw,
    Search,
} from 'lucide-react';
import { getAdminTripsCheckClaimsFullList } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { buildStayWeatherPivotTables } from '../../utils/tripsCheckClaimsWeatherPivot';
import {
    exportWeatherCsv,
    exportWeatherExcel,
    exportWeatherPdf,
} from '../../utils/weatherExport';
import {
    WeatherBreadcrumb,
    WeatherClientSummary,
    WeatherDetailsSkeleton,
    WeatherPrecipitationTable,
    WeatherStatCards,
    WeatherTemperatureTable,
} from '../../components/weather';
import usePersistedSearchState from '../../hooks/usePersistedSearchState';
import 'bootstrap/dist/css/bootstrap.css';
import '../../components/weather/weatherDetails.css';

const EMPTY_WEATHER_DETAILS_FILTERS = {
    policy_code: '',
    reservation_number: '',
    client_email: '',
};

const hasAppliedSearch = (applied) => Boolean(
    String(applied?.policy_code || '').trim()
    || String(applied?.reservation_number || '').trim()
);

/**
 * Weather Details — observations horaires (design SaaS premium).
 * Route : /admin/trips/check-claims/weather-details
 *
 * Note : le projet utilise Bootstrap (pas MUI). UI inspirée Stripe / Linear / Shadcn.
 */
const AdminTripsCheckClaimWeatherDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const initialFromState = location.state?.filters && typeof location.state.filters === 'object'
        ? location.state.filters
        : {};

    const {
        filters,
        setFilters,
        appliedFilters,
        setAppliedFilters,
        clearSearchState,
    } = usePersistedSearchState('trips-check-claim-weather-details', EMPTY_WEATHER_DETAILS_FILTERS);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [payload, setPayload] = useState(null);

    useEffect(() => {
        const policy = String(searchParams.get('policy_code') || '').trim();
        const reservation = String(searchParams.get('reservation_number') || '').trim();
        const email = String(searchParams.get('client_email') || '').trim();
        if (policy || reservation) {
            const urlFilters = {
                policy_code: policy,
                reservation_number: reservation,
                client_email: email,
            };
            setFilters(urlFilters);
            setAppliedFilters(urlFilters);
            return;
        }
        const statePolicy = String(initialFromState.policy_code || '').trim();
        const stateReservation = String(initialFromState.reservation_number || '').trim();
        const stateEmail = String(initialFromState.client_email || '').trim();
        if (statePolicy || stateReservation || stateEmail) {
            setFilters({
                policy_code: statePolicy,
                reservation_number: stateReservation,
                client_email: stateEmail,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleClearFilters = () => {
        clearSearchState();
        setPayload(null);
        setError(null);
        setSearchParams({});
    };

    const canShow = Boolean(
        String(filters.policy_code || '').trim()
        || String(filters.reservation_number || '').trim()
    );

    const handleApplyFilters = (e) => {
        e?.preventDefault?.();
        const policy = String(filters.policy_code || '').trim();
        const reservation = String(filters.reservation_number || '').trim();
        const email = String(filters.client_email || '').trim();
        if (!policy && !reservation) {
            setError('Veuillez renseigner au moins le code police ou le numéro de réservation.');
            setAppliedFilters({ ...EMPTY_WEATHER_DETAILS_FILTERS });
            setPayload(null);
            return;
        }
        setError(null);
        setAppliedFilters({
            policy_code: policy,
            reservation_number: reservation,
            client_email: email,
        });
        const params = {};
        if (policy) params.policy_code = policy;
        if (reservation) params.reservation_number = reservation;
        if (email) params.client_email = email;
        setSearchParams(params);
    };

    const loadData = useCallback(async () => {
        if (!hasAppliedSearch(appliedFilters)) {
            setPayload(null);
            return;
        }
        const canLoad = Boolean(
            appliedFilters.policy_code || appliedFilters.reservation_number
        );
        if (!canLoad) {
            setPayload(null);
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const params = {};
            if (appliedFilters.policy_code) params.policy_code = appliedFilters.policy_code;
            if (appliedFilters.reservation_number) {
                params.reservation_number = appliedFilters.reservation_number;
            }
            if (appliedFilters.client_email) params.client_email = appliedFilters.client_email;
            const data = await getAdminTripsCheckClaimsFullList(params);
            setPayload(data);
        } catch (err) {
            setPayload(null);
            setError(handleApiError(err, 'Erreur lors du chargement des observations météo'));
        } finally {
            setLoading(false);
        }
    }, [appliedFilters]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const pivots = useMemo(
        () => (payload ? buildStayWeatherPivotTables(payload) : null),
        [payload]
    );

    const hasObservations = Boolean(
        pivots
        && ((pivots.precipitation?.count || 0) + (pivots.temperature?.count || 0) > 0
            || pivots.checkClaimsCount > 0)
    );

    const fileBase = `weather_${appliedFilters?.policy_code || 'policy'}_${appliedFilters?.reservation_number || 'res'}`
        .replace(/[^\w.-]+/g, '_');

    const handleExportCsv = () => {
        exportWeatherCsv({
            precipitation: pivots?.precipitation,
            temperature: pivots?.temperature,
            fileBase,
        });
    };

    const handleExportExcel = () => {
        exportWeatherExcel({
            precipitation: pivots?.precipitation,
            temperature: pivots?.temperature,
            summary: pivots?.summary,
            stats: pivots?.stats,
            fileBase,
        });
    };

    return (
        <div className="wd-page">
            <WeatherBreadcrumb />

            <header className="wd-header wd-fade-in">
                <div>
                    <div className="wd-header__title-row">
                        <div className="wd-header__icon" aria-hidden="true">
                            <CloudSun size={24} strokeWidth={2} />
                        </div>
                        <div>
                            <h1 className="wd-header__title">Weather Details</h1>
                            <p className="wd-header__subtitle">
                                Observations horaires de précipitations et de températures
                                pour toute la durée du séjour.
                            </p>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    className="wd-btn-back wd-no-print"
                    onClick={() => navigate('/admin/trips/check-claims')}
                >
                    <ArrowLeft size={16} aria-hidden="true" />
                    Retour à la liste
                </button>
            </header>

            {error && (
                <div className="wd-alert wd-alert--error" role="alert">
                    {error}
                    <button
                        type="button"
                        className="wd-btn wd-btn--ghost wd-btn--sm ms-2"
                        onClick={() => setError(null)}
                        aria-label="Fermer l'alerte"
                    >
                        Fermer
                    </button>
                </div>
            )}

            <form className="wd-filters wd-no-print wd-fade-in" onSubmit={handleApplyFilters}>
                <div className="wd-filters__label">Filtres</div>
                <div className="wd-filters__row">
                    <div className="wd-filters__field">
                        <label htmlFor="wd-policy-code">Code police (optionnel)</label>
                        <input
                            id="wd-policy-code"
                            name="policy_code"
                            value={filters.policy_code}
                            onChange={handleFilterChange}
                            placeholder="Ex: 301826051"
                            autoComplete="off"
                        />
                    </div>
                    <div className="wd-filters__field">
                        <label htmlFor="wd-reservation">Numéro de réservation (optionnel)</label>
                        <input
                            id="wd-reservation"
                            name="reservation_number"
                            value={filters.reservation_number}
                            onChange={handleFilterChange}
                            placeholder="Numéro de réservation"
                            autoComplete="off"
                        />
                    </div>
                    <div className="wd-filters__field">
                        <label htmlFor="wd-client-email">Email client (optionnel)</label>
                        <input
                            id="wd-client-email"
                            name="client_email"
                            type="email"
                            value={filters.client_email}
                            onChange={handleFilterChange}
                            placeholder="client@exemple.com"
                            autoComplete="off"
                        />
                    </div>
                    <div className="wd-filters__actions">
                        <button
                            type="submit"
                            className="wd-btn wd-btn--primary"
                            disabled={loading || !canShow}
                        >
                            {loading
                                ? <RefreshCw size={16} className="wd-spin" aria-hidden="true" />
                                : <Search size={16} aria-hidden="true" />}
                            Afficher
                        </button>
                        <button
                            type="button"
                            className="wd-btn wd-btn--secondary"
                            onClick={loadData}
                            disabled={loading || !hasAppliedSearch(appliedFilters)}
                        >
                            <RefreshCw size={16} className={loading ? 'wd-spin' : ''} aria-hidden="true" />
                            Actualiser
                        </button>
                        <button
                            type="button"
                            className="wd-btn wd-btn--outlined"
                            onClick={handleClearFilters}
                            disabled={loading}
                        >
                            <RotateCcw size={16} aria-hidden="true" />
                            Réinitialiser
                        </button>
                    </div>
                </div>

                <details className="wd-dev">
                    <summary>
                        <Code2 size={12} aria-hidden="true" />
                        Developer Info
                    </summary>
                    <div className="wd-dev__body">
                        <div>GET /api/trips/check-claims</div>
                        <div>Params: policy_code ou reservation_number (≥ 1 requis), client_email (optionnel)</div>
                        {hasAppliedSearch(appliedFilters) && (
                            <div className="mt-1">
                                Active →
                                {[
                                    appliedFilters.policy_code && `policy_code=${appliedFilters.policy_code}`,
                                    appliedFilters.reservation_number && `reservation_number=${appliedFilters.reservation_number}`,
                                    appliedFilters.client_email && `client_email=${appliedFilters.client_email}`,
                                ].filter(Boolean).join(' · ') || '—'}
                            </div>
                        )}
                    </div>
                </details>
            </form>

            {!hasAppliedSearch(appliedFilters) && !loading && (
                <div className="wd-alert wd-alert--info" role="status">
                    Saisissez au moins un <strong>code police</strong> ou un <strong>numéro de réservation</strong> (email optionnel), puis cliquez sur Afficher.
                </div>
            )}

            {loading && <WeatherDetailsSkeleton />}

            {!loading && hasAppliedSearch(appliedFilters) && pivots && !hasObservations && (
                <div className="wd-alert wd-alert--empty" role="status">
                    <div className="wd-empty-icon" aria-hidden="true">
                        <Inbox size={22} />
                    </div>
                    <div className="fw-semibold mb-1">Aucune observation météo</div>
                    <div>Aucun résultat pour ces filtres. Vérifiez le code police (et le numéro de réservation si renseigné).</div>
                </div>
            )}

            {!loading && hasObservations && (
                <div className="wd-fade-in">
                    <WeatherClientSummary summary={pivots.summary} />
                    <WeatherStatCards stats={pivots.stats} />

                    <div className="d-flex flex-column gap-4">
                        <WeatherPrecipitationTable
                            pivot={pivots.precipitation}
                            onRefresh={loadData}
                            refreshing={loading}
                            onExportCsv={handleExportCsv}
                            onExportExcel={handleExportExcel}
                            onPrint={exportWeatherPdf}
                        />
                        <WeatherTemperatureTable
                            pivot={pivots.temperature}
                            onRefresh={loadData}
                            refreshing={loading}
                            onExportCsv={handleExportCsv}
                            onExportExcel={handleExportExcel}
                            onPrint={exportWeatherPdf}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminTripsCheckClaimWeatherDetails;
